import { Injectable, type Provider } from '@angular/core';

import { kindLabel } from '../feed/hub-tile.vm';
import { HubPageService, type HubMemberRow, type HubNotifyLevel, type HubPageView, type HubPictureSave, type HubResource } from './hub-page.service';
import type { ImageFrame } from '../../../shared/media/image-frame';
import { err, makeError, ok, type ApiResult } from '../../../core/models/api-result';
import { PREVIEW_PERSON_NAMES } from '../create/hub-create-preview.service';

/**
 * THE HUB PAGE, WITHOUT A SESSION.
 *
 * Same bargain as `HubFeedPreviewService`: `hub()` is viewer-scoped, so the
 * guard-free harness would otherwise show an error banner where the sheet
 * should be. The hub is derived from the corpus the wall is already showing,
 * so the sheet and the tiles behind it agree about the name, the class and
 * the post count — a preview that disagreed with itself would be worse than
 * no preview.
 *
 * The purposes and moderators below are the only invented content in the
 * harness, and they are here rather than in the fixture file because they are
 * a property of THIS surface: the corpus has no hub-page copy to transcribe.
 */
const PREVIEW_PURPOSE: Record<string, string> = {
  bhm: 'Everything about the building — what is open, what is moving, what is broken, and who to ask. If it affects your day at Rum Runner Works, it is posted here.',
  rdx: 'What the product and platform teams are shipping, and why. Sprint notes, design decisions and the reasoning behind the ones that were reversed.',
  eu: 'The region, quarter by quarter. Results, leadership, and anything that changes how Ricoh Europe works rather than how one office does.',
};

const PREVIEW_MODS: Record<string, { _id: string; name: string; role: string }[]> = {
  bhm: [
    { _id: 'm1', name: 'Callum Rees', role: 'owner' },
    { _id: 'm2', name: 'Sofia Marin', role: 'manager' },
  ],
  rdx: [
    { _id: 'm3', name: 'Marcus Webb', role: 'owner' },
    { _id: 'm4', name: 'Priya Raman', role: 'manager' },
  ],
  // The harness viewer OWNS this one, so the owner-only Delete in Hub
  // settings is reviewable; Rum Runner Works is an office hub and has none.
  coffee: [
    { _id: 'preview-viewer', name: 'You', role: 'owner' },
    { _id: 'm5', name: 'Sofia Marin', role: 'manager' },
  ],
};

/** A location hub's building, for its events and the week nudge. */
const PREVIEW_HUB_LOCATION: Record<string, string> = {
  bhm: 'loc-rrw',
};

/** Hubs the harness viewer may post to: "New event" shows on these only. */
const PREVIEW_CAN_POST = new Set(['bhm', 'coffee', 'run', 'lab']);

const PREVIEW_COVERS: Record<string, string> = {
  bhm: '/assets/hubs-preview/pro-atrium.jpg',
  rdx: '/assets/hubs-preview/pro-skyline.jpg',
  lab: '/assets/hubs-preview/pro-lab.jpg',
};

const PREVIEW_GUIDELINES: Record<string, string[]> = {
  bhm: [
    'Say which floor you are on — it saves three replies.',
    'Photographs of people need their nod first.',
    'Anything urgent goes to the service desk, not here.',
  ],
  rdx: [
    'Link the ticket, not a screenshot of the ticket.',
    'Decisions get written up here within the day.',
  ],
};

const PREVIEW_LINKS: Record<string, HubResource[]> = {
  // Smart links (11 Oct 2026): a main link and three tools, so the cover's
  // pills, the lead and the "+N" fold are all reviewable on a hub the harness
  // viewer may edit.
  coffee: [
    {
      id: 'c1',
      title: 'Order beans',
      url: 'https://beans.example.test/ricoh',
      description: null,
      kind: 'link',
      featured: true,
    },
    {
      id: 'c2',
      title: '#coffee-club',
      url: 'https://ricoh-rdx.slack.com/archives/C04COFFEE',
      description: null,
      kind: 'link',
    },
    {
      id: 'c3',
      title: 'New grinder',
      url: 'https://linear.app/rdx/project/new-grinder-8c1d2e',
      description: null,
      kind: 'link',
    },
    {
      id: 'c4',
      title: 'Cleaning rota',
      url: 'https://docs.google.com/spreadsheets/d/1abc/edit',
      description: null,
      kind: 'link',
    },
    {
      id: 'c5',
      title: 'Machine faults',
      url: 'https://ricoh.atlassian.net/jira/software/projects/CAFE/boards/12',
      description: null,
      kind: 'link',
    },
  ],
  bhm: [
    // The place hero's strip (board 05): a main link, three tools with
    // their own marks, and the two older links behind "+N".
    {
      id: 'l0',
      title: 'Book a desk here',
      url: 'https://example.test/book?site=rum-runner',
      description: null,
      kind: 'link',
      featured: true,
    },
    {
      id: 'l3',
      title: 'Works noticeboard',
      url: 'https://teams.microsoft.com/l/channel/19%3Aworks/Noticeboard',
      description: null,
      kind: 'link',
    },
    {
      id: 'l4',
      title: 'Report a fault',
      url: 'https://ricoh.atlassian.net/jira/software/projects/FAC/boards/3',
      description: null,
      kind: 'link',
    },
    {
      id: 'l1',
      title: 'Floor plans and room finder',
      url: 'https://example.test/floors',
      description: 'Every level, updated after the Level 3 move',
      kind: 'link',
    },
    {
      id: 'l2',
      title: 'Report something broken',
      url: 'https://example.test/facilities',
      description: 'Goes to the facilities queue, not to this hub',
      kind: 'link',
    },
  ],
};


/**
 * Hubs the harness viewer joined from an invite or a join link this session
 * (hub invites, 12 Oct 2026). `HubInvitesPreviewService.sendAccept` adds to
 * it; `load` reads it, so the cover says Joined after the join card closes,
 * as the server's read would.
 */
export const PREVIEW_JOINED_HUBS = new Set<string>();

/** Hubs the harness viewer may invite to: custom hubs they run or belong to. */
const PREVIEW_CAN_INVITE = new Set(['coffee', 'lab']);

/** Pending invites, which the server shows to managers only. */
const PREVIEW_PENDING_INVITES: Record<string, number> = { coffee: 3 };

/** A preview hub's purpose and links, for the Everything rail's office card. */
export function previewHubSummary(hubId: string): { purpose: string | null; resources: HubResource[] } {
  return { purpose: PREVIEW_PURPOSE[hubId] ?? null, resources: PREVIEW_LINKS[hubId] ?? [] };
}

@Injectable()
export class HubPagePreviewService extends HubPageService {
  override async load(hubId: string): Promise<void> {
// ⚠ LAZY, AND THE WHOLE POINT OF THIS FILE'S EXISTENCE IS WHY.
// `app.routes.ts` imports the preview PROVIDER as a value, so anything this
// module imports at the top level lands in the EAGER `main.js` — for every
// visitor, on every route, whether or not they ever open `/preview/hubs`. The
// fixture corpus is 84 kB of prototype content. Imported here, inside the one
// async method that reads it, the module registry still dedupes it across the
// preview services and nobody who is not previewing pays for any of it.
    const { HUBS_PREVIEW_ITEMS } = await import('./hubs-preview.fixtures');
    const posts = HUBS_PREVIEW_ITEMS.filter(i => i.hub?._id === hubId);
    const chip = posts[0]?.hub;
    if (!chip) {
      // A hub with nothing in the corpus reads as absent rather than as a
      // blank sheet — the same answer the server gives for one you may not
      // read, and the sheet's error banner is the right surface for it.
      this._state.setData(null);
      return;
    }

    this._state.setData({
      _id: chip._id,
      name: chip.name,
      type: chip.type,
      description: null,
      purpose: PREVIEW_PURPOSE[hubId] ?? null,
      // Offices and divisions are the ones People and Culture vouch for in the
      // corpus; a club is not policy.
      verified: chip.type !== 'custom',
      // The wire's value, not the enum's TS one — see `HubVisibilityWire`.
      visibility: 'OPEN',
      memberCount: chip.memberCount,
      postCount: posts.length,
      // The harness viewer OWNS Coffee Club (see PREVIEW_MODS), and an owner
      // is offered no Leave, as on the server.
      memberRole: hubId === 'coffee' ? 'owner' : chip.memberRole,
      // THE SERVER'S MEMBERSHIP VERDICT, which the harness never gave, so Join
      // and Leave could not be reviewed here at all ("unknown" draws nothing).
      // Offices and divisions are automatic; a club is joined when the corpus
      // says the reader has a role in it (journey audit, 9 Oct 2026).
      membership:
        chip.type === 'custom' ? (chip.memberRole ? 'JOINED' : 'NONE') : 'AUTOMATIC',
      // ⚠ TRUE FOR ONE HUB, NOT ALL OF THEM. The pin editor's whole design is
      // that it is ABSENT for a reader without the right, and a harness where
      // every hub is manageable can only ever show the half that is easy to
      // get correct. Walk to any other hub and the Edit control should vanish.
      // Two hubs: the office hub (to curate) and the club the harness viewer
      // owns (to delete). Every other hub has no Edit at all.
      canManage: hubId === 'bhm' || hubId === 'coffee',
      // Hub invites (12 Oct 2026): Coffee Club, which the viewer owns, and
      // the Lab, an open club they belong to. Never an office or division:
      // their membership is derived, and the server refuses invites there.
      canInvite: PREVIEW_CAN_INVITE.has(hubId),
      pendingInviteCount: PREVIEW_PENDING_INVITES[hubId] ?? null,
      allowMemberInvites: chip.type === 'custom' ? true : false,
      canPost: PREVIEW_CAN_POST.has(hubId),
      locationId: PREVIEW_HUB_LOCATION[hubId] ?? null,
      notifyLevel: 'EVERYTHING',
        // A real photograph for three hubs and none for the rest, so BOTH
        // cover states are reviewable — the 34-of-62 overlap only means
        // anything over a picture, and the bare variant is what most hubs
        // show until somebody uploads one.
        coverImage: PREVIEW_COVERS[hubId] ?? null,
        // The harness draws initials, deliberately: a preview hub has no
        // uploaded mark, and inventing one here would hide the fallback that
        // most real hubs actually use.
        logo: null,
        // The harness draws the default policy; the preview has no settings.
        postingPolicy: null,
      primaryColor: null,
      moderators: PREVIEW_MODS[hubId] ?? [],
        // Written for two hubs, absent for the rest — so the rail's "nobody
        // has written these yet" state is reviewable too. `null` and `[]`
        // render differently and both need to be looked at.
        guidelines: PREVIEW_GUIDELINES[hubId] ?? null,
        resources: PREVIEW_LINKS[hubId] ?? [],
    });

      // WHO IS HERE. Moderators first, then names drawn from the corpus's own
      // authors — so the faces on the rail are people who have actually posted
      // in this hub rather than a second invented cast that could contradict it.
      const authors = new Map<string, { name: string; jobTitle: string | null }>();
      for (const post of posts) {
        const a = post.author;
        if (!a || authors.has(a._id)) continue;
        authors.set(a._id, {
          name: [a.firstName, a.lastName].filter(Boolean).join(' '),
          jobTitle: a.jobTitle,
        });
      }
      const mods = PREVIEW_MODS[hubId] ?? [];
      const modNames = new Set(mods.map(m => m.name));
      this._members.setData([
        ...mods.map(m => ({
          _id: m._id,
          name: m.name,
          role: m.role,
          jobTitle: null,
          picture: null,
        })),
        ...[...authors.entries()]
          .filter(([, a]) => a.name && !modNames.has(a.name))
          .slice(0, 10)
          .map(([id, a]) => ({
            _id: id,
            name: a.name,
            role: 'member',
            jobTitle: a.jobTitle,
            picture: null,
          })),
      ]);
      // Deliberately LARGER than the list. The rail reads "12 of 412", and a
      // total equal to the page is the one case that never exercises that line.
      this.setMemberTotal(chip.memberCount ?? 0);

    const edited = this.edits.get(hubId);
    const loaded = this._state.peek();
    if (edited && loaded) this._state.setData({ ...loaded, ...edited } as typeof loaded);

    // Joined from an invite or a link in this session.
    const now = this._state.peek();
    if (now && PREVIEW_JOINED_HUBS.has(hubId) && now.membership === 'NONE') {
      this._state.setData({
        ...now,
        membership: 'JOINED',
        memberRole: 'member',
        memberCount: (now.memberCount ?? 0) + 1,
      });
    }

    this._pinned.setData(
      posts.slice(0, 4).map((p, i) => ({
        id: p.id,
        title: p.title,
        publishedAt: p.publishedAt,
        imageUrl: p.media?.image?.url ?? p.media?.video?.posterUrl ?? null,
        kindLabel: kindLabel(p),
        hubName: p.hub?.name ?? null,
      })),
    );
  }

  /**
   * Edits made in the harness, laid back over the fixture on every load so a
   * new icon or a new purpose survives the page re-reading the hub, as the
   * server's would.
   */
  private readonly edits = new Map<string, Record<string, unknown>>();

  override async updateHub(
    hubId: string,
    patch: Parameters<HubPageService['updateHub']>[1],
  ): Promise<boolean> {
    this.edits.set(hubId, { ...(this.edits.get(hubId) ?? {}), ...patch });
    const current = this._state.peek();
    if (current?._id === hubId) this._state.setData({ ...current, ...patch } as typeof current);
    return true;
  }

  /** A picture and its position, kept in the harness the way the server keeps them. */
  override async savePicture(
    hubId: string,
    field: 'logo' | 'coverImage',
    url: string | null,
    frame: ImageFrame | null,
  ): Promise<HubPictureSave> {
    const frameKey = field === 'logo' ? 'logoFrame' : 'coverFrame';
    const patch: Record<string, unknown> = { [frameKey]: frame };
    if (url !== null) patch[field] = url;
    await this.updateHub(hubId, patch as Parameters<HubPageService['updateHub']>[1]);
    return 'saved';
  }

  /** The harness's own hub rosters: the faces on the rail, with roles. */
  private readonly rosters = new Map<string, HubMemberRow[]>();

  private rosterFor(hubId: string): HubMemberRow[] {
    let roster = this.rosters.get(hubId);
    if (!roster) {
      const faces = this._members.peek() ?? [];
      // The faces already carry the moderators first (the harness viewer owns
      // Coffee Club), then the people who have posted.
      const roleOf = (role: string | null): string => {
        const r = (role ?? '').toLowerCase();
        if (r === 'owner') return 'OWNER';
        if (r === 'manager' || r === 'moderator') return 'MANAGER';
        return 'MEMBER';
      };
      roster = faces.map(face => ({
        userId: face._id,
        name: face.name,
        picture: face.picture,
        role: roleOf(face.role),
      }));
      this.rosters.set(hubId, roster);
    }
    return roster;
  }

  protected override async fetchMemberRows(hubId: string): Promise<ApiResult<readonly HubMemberRow[] | null>> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return ok([...this.rosterFor(hubId)]);
  }

  protected override async sendAddMember(hubId: string, userId: string): Promise<ApiResult<unknown>> {
    await new Promise(resolve => setTimeout(resolve, 150));
    const roster = this.rosterFor(hubId);
    if (!roster.some(r => r.userId === userId)) {
      roster.push({ userId, name: PREVIEW_PERSON_NAMES[userId] ?? 'A colleague', picture: null, role: 'MEMBER' });
    }
    return ok({ _id: hubId });
  }

  protected override async sendRemoveMember(hubId: string, userId: string): Promise<ApiResult<unknown>> {
    await new Promise(resolve => setTimeout(resolve, 150));
    const roster = this.rosterFor(hubId);
    const at = roster.findIndex(r => r.userId === userId);
    if (at >= 0 && roster[at].role === 'OWNER') {
      return err(makeError('FORBIDDEN', 'The owner cannot be removed — transfer ownership or delete the hub.', { retryable: false }));
    }
    if (at >= 0) roster.splice(at, 1);
    return ok({ _id: hubId });
  }

  /**
   * Pinning, in the harness: the rail gains or loses the post, from the
   * corpus, as the server's pinned read would. Without this the reader's Pin
   * answered "That did not save" on every press here.
   */
  /**
   * The link writes, held in the harness. The same splice the real path does
   * (`applyLinks`), over a list edited here instead of by the server — so the
   * pills, the panel and the sheet are reviewed against the code that runs
   * signed in, not a second copy of it.
   */
  protected override async writeLinks(
    mutation: string,
    variables: Record<string, unknown>,
  ): Promise<{ ok: true } | { ok: false; message: string }> {
    const hubId = String(variables['hubId']);
    const current = [...(this._state.peek()?.resources ?? [])];
    const resourceId = variables['resourceId'] as string | undefined;
    const input = (variables['input'] ?? {}) as {
      url?: string;
      title?: string;
      featured?: boolean;
    };
    let next: HubResource[];
    if (mutation.includes('addHubResource')) {
      const added: HubResource = {
        id: `preview-${Date.now()}`,
        title: input.title ?? null,
        url: input.url ?? '',
        description: null,
        kind: 'link',
        featured: input.featured === true,
      };
      next = [...current.map(l => (added.featured ? { ...l, featured: false } : l)), added];
    } else if (mutation.includes('removeHubResource')) {
      next = current.filter(l => l.id !== resourceId);
    } else {
      next = current.map(l => {
        const target = l.id === resourceId;
        let link = l;
        if (target && input.title !== undefined) link = { ...link, title: input.title || null };
        if (input.featured === true) link = { ...link, featured: target };
        else if (input.featured === false && target) link = { ...link, featured: false };
        return link;
      });
    }
    this.applyLinks(hubId, next);
    return { ok: true };
  }

  override async setPinned(hubId: string, postId: string, pinned: boolean): Promise<boolean> {
    const current = this._pinned.peek() ?? [];
    if (!pinned) {
      this._pinned.setData(current.filter(p => p.id !== postId));
      return true;
    }
    if (current.some(p => p.id === postId)) return true;
    const { HUBS_PREVIEW_ITEMS } = await import('./hubs-preview.fixtures');
    const post = HUBS_PREVIEW_ITEMS.find(i => i.id === postId);
    if (!post || post.hub?._id !== hubId) return false;
    this._pinned.setData([
      {
        id: post.id,
        title: post.title,
        publishedAt: post.publishedAt,
        imageUrl: post.media?.image?.url ?? post.media?.video?.posterUrl ?? null,
        kindLabel: kindLabel(post),
        hubName: post.hub?.name ?? null,
      },
      ...current,
    ]);
    return true;
  }

  /** The upload, in the harness: the picture stays in the browser. */
  protected override async sendImage(_hubId: string, file: File): Promise<{ url: string }> {
    return { url: URL.createObjectURL(file) };
  }

  override async toggleJoin(hubId: string, joined: boolean): Promise<void> {
    const current = this.hub();
    if (!current) return;
    this._state.setData({
      ...current,
      membership: joined ? 'NONE' : 'JOINED',
      memberRole: joined ? null : 'member',
      memberCount: (current.memberCount ?? 0) + (joined ? -1 : 1),
      notifyLevel: joined ? 'NOTHING' : 'EVERYTHING',
    });
  }

  override async deleteHub(hubId: string): Promise<{ ok: true } | { ok: false; message: string }> {
    // Nothing is written in the harness; the page leaves the hub as it would.
    if (this.hub()?._id === hubId) this.clear();
    return { ok: true };
  }

  override async setNotifyLevel(
    hubId: string,
    level: HubNotifyLevel,
  ): Promise<void> {
    const current = this.hub();
    if (!current) return;
    // Mirrors the server: setting a level on a hub you are not in JOINS you,
    // because asking to be told about a hub is what joining means. `NOTHING`
    // on a hub you are not in changes nothing.
    const joins = level !== 'NOTHING' && !current.memberRole;
    this._state.setData({
      ...current,
      notifyLevel: level,
      memberRole: joins ? 'member' : current.memberRole,
      memberCount: (current.memberCount ?? 0) + (joins ? 1 : 0),
    });
  }

}

/** Drop into a route's `providers` to serve the corpus instead of the API. */
export const HUB_PAGE_PREVIEW_PROVIDER: Provider = {
  provide: HubPageService,
  useClass: HubPagePreviewService,
};
